/**
 * Telegram小程序启动链接生成工具
 * 用于生成带有启动参数的Telegram WebApp链接
 */

export interface TelegramLinkOptions {
  botUsername: string;
  appName: string;
  page?: string;
  params?: Record<string, string>;
}

/**
 * 生成Telegram WebApp启动链接
 * @param options 链接配置选项
 * @returns 完整的Telegram WebApp链接
 */
export function generateTelegramLink(options: TelegramLinkOptions): string {
  const { botUsername, appName, page, params = {} } = options;

  // 构建完整链接
  const baseUrl = `https://t.me/${botUsername}/${appName}`;

  // Telegram WebApp 深度链接使用 startapp 参数传递启动参数
  if (page) {
    const queryParams = new URLSearchParams();
    queryParams.set('startapp', page);

    // 添加其他参数
    Object.entries(params).forEach(([key, value]) => {
      queryParams.set(key, value);
    });

    const queryString = queryParams.toString();
    return `${baseUrl}?${queryString}`;
  }

  return baseUrl;
}

/**
 * 预定义的页面链接生成器
 */
export const TelegramLinks = {
  /**
   * 跳转到首页
   */
  home: (botUsername: string, appName: string) => generateTelegramLink({ botUsername, appName, page: 'home' }),

  /**
   * 跳转到量化账户页面
   */
  quantAccount: (botUsername: string, appName: string) =>
    generateTelegramLink({ botUsername, appName, page: 'quant-account' }),

  /**
   * 跳转到钱包页面
   */
  wallet: (botUsername: string, appName: string) => generateTelegramLink({ botUsername, appName, page: 'wallet' }),

  /**
   * 跳转到充值页面
   */
  deposit: (botUsername: string, appName: string) => generateTelegramLink({ botUsername, appName, page: 'deposit' }),

  /**
   * 跳转到提现页面
   */
  withdraw: (botUsername: string, appName: string) => generateTelegramLink({ botUsername, appName, page: 'withdraw' }),

  /**
   * 跳转到收益页面
   */
  profit: (botUsername: string, appName: string) => generateTelegramLink({ botUsername, appName, page: 'profit' }),

  /**
   * 跳转到设置页面
   */
  settings: (botUsername: string, appName: string) => generateTelegramLink({ botUsername, appName, page: 'settings' }),

  /**
   * 跳转到API测试页面
   */
  apiTest: (botUsername: string, appName: string) => generateTelegramLink({ botUsername, appName, page: 'api-test' }),
};

/**
 * 支持的页面列表
 */
export const SUPPORTED_PAGES = [
  'home',
  'dashboard',
  'quant-account',
  'wallet',
  'deposit',
  'withdraw',
  'profit',
  'settings',
  'api-test',
  'link-generator',
  'tools',
  'price-alerts-history',
  'watchlist',
  'stock-monitors',
  'stock-dashboard',
  'stock-strategies',
  'stock-quant-portfolio',
  'stock-portfolio',
  'research-reports',
  'stock-signals',
] as const;

export type SupportedPage = (typeof SUPPORTED_PAGES)[number];

/**
 * 验证页面参数是否有效
 * @param page 页面名称
 * @returns 是否为有效页面
 */
export function isValidPage(page: string): page is SupportedPage {
  return SUPPORTED_PAGES.includes(page as SupportedPage);
}

/**
 * 获取页面显示名称
 * @param page 页面名称
 * @returns 页面显示名称
 */
export function getPageDisplayName(page: SupportedPage): string {
  const displayNames: Record<SupportedPage, string> = {
    'home': '首页',
    'dashboard': '仪表板',
    'quant-account': '量化账户',
    'wallet': '钱包',
    'deposit': '充值',
    'withdraw': '提现',
    'profit': '收益',
    'settings': '设置',
    'api-test': 'API测试',
    'link-generator': '链接生成器',
    'tools': '工具页面',
    'price-alerts-history': '价格警报历史',
    'watchlist': '我的关注',
    'stock-monitors': 'A股监控',
    'stock-dashboard': 'A股仪表板',
    'stock-strategies': '选股策略',
    'stock-quant-portfolio': '量化组合',
    'stock-portfolio': '股票组合',
    'research-reports': 'AI研报',
    'stock-signals': '核心信号',
  };

  return displayNames[page] || page;
}

/**
 * 示例用法和链接生成
 */
export const ExampleLinks = {
  // 示例配置
  botUsername: 'your_bot_username',
  appName: 'crypto_quant_app',

  // 生成各种链接
  get examples() {
    return {
      home: TelegramLinks.home(this.botUsername, this.appName),
      quantAccount: TelegramLinks.quantAccount(this.botUsername, this.appName),
      wallet: TelegramLinks.wallet(this.botUsername, this.appName),
      deposit: TelegramLinks.deposit(this.botUsername, this.appName),
      withdraw: TelegramLinks.withdraw(this.botUsername, this.appName),
      profit: TelegramLinks.profit(this.botUsername, this.appName),
      settings: TelegramLinks.settings(this.botUsername, this.appName),
      apiTest: TelegramLinks.apiTest(this.botUsername, this.appName),
    };
  },
};
